import { Component, Prop, h, Host, Build } from '@stencil/core';
import { ACTION_REGISTRY, type ActionName } from '../../../../../actions';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'call-to-action-type-2';

/**
 * Call-to-action section with label, heading, action buttons, and a media illustration.
 * Two-column layout on desktop (text left, image right), stacked on mobile.
 * Supports pure slot pass-through for inline editing in the visual editor.
 *
 * @product signnow
 * @version 2.0.0
 * @category SignNow
 * @contentConfig Call-to-action component for encouraging user engagement and conversions
 * @example
 *   <sn-call-to-action-type-2>
 *     <me-text slot="label" tag="span" class="me-text--label">be ready to get more</me-text>
 *     <me-heading slot="heading" variant="2" tag="h4">Get legally-binding signatures now!</me-heading>
 *     <me-button slot="action-primary" variant="primary" href="https://snseats.signnow.com/purchase/business_free_trial/loggedout">Start free trial</me-button>
 *     <me-button slot="action-secondary" variant="bordered" href="/contact">Request a call</me-button>
 *     <me-picture slot="media" src-set="https://marketing-static.signnow.com/329/images/e-signature/Illustration@2x.png 2x, https://marketing-static.signnow.com/329/images/e-signature/Illustration.png 1x" webp-src-set="https://marketing-static.signnow.com/329/images/e-signature/Illustration@2x.webp 2x, https://marketing-static.signnow.com/329/images/e-signature/Illustration.webp 1x" aspect-ratio="16/9" alt="Illustration"></me-picture>
 *   </sn-call-to-action-type-2>
 * @slot label - <me-text tag="span" class="me-text--label"> - Small label displayed above the heading
 * @slot heading - {required}, <me-heading variant="2" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Main CTA heading
 * @slot action-primary - {required}, <me-button variant="primary"> - Primary action button. Either a plain link (`href`)
 *   or an on-page action: set `trigger-actions='["nav:anchor-scroll"]'` with `action-data='{"nav:anchor-scroll":{"anchorId":"form-user-data"}}'`
 *   to smoothly scroll to a form on the page instead of navigating.
 * @slot action-secondary - <me-button variant="bordered"> - Secondary action button (plain link or trigger-action, same as action-primary)
 * @slot media - {required}, <me-picture> - Illustration or hero image
 */
@Component({
  tag: 'sn-call-to-action-type-2',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNCallToActionType2 {
  /**
   * Color mode for theming
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Runs a slotted action button's `trigger-actions` (e.g. `nav:anchor-scroll`) on click.
   * Handled here — rather than relying on `me-button`'s own click handler — because the
   * `trigger-actions` / `action-data` props arrive as JSON-string attributes after SSR/hydration
   * and are not bound back onto the button's properties there. Reads the attribute first
   * (authoritative on the server-rendered page), then falls back to the property (set via
   * property binding, e.g. Storybook). Plain links and form-trigger buttons are left untouched.
   */
  private handleActionClick = async (event: Event) => {
    if (!Build.isBrowser) return;

    const actionEl = (event.target as HTMLElement | null)?.closest('[trigger-actions]') as
      | (HTMLElement & { triggerActions?: unknown; actionData?: unknown })
      | null;
    if (!actionEl) return;

    event.preventDefault();
    const actions = parseJsonProp(actionEl.getAttribute('trigger-actions') ?? actionEl.triggerActions, []) as ActionName[];
    const data = parseJsonProp(actionEl.getAttribute('action-data') ?? actionEl.actionData, {}) as Record<string, unknown>;

    for (const name of actions) {
      const loadAction = ACTION_REGISTRY[name];
      if (!loadAction) continue;
      const { default: Action } = await loadAction();
      await new Action().execute({
        event: event as MouseEvent,
        element: actionEl,
        component: this,
        data: data?.[name] ?? data,
      });
    }
  };

  render() {
    return (
      <Host data-color-mode={this.colorMode}>
        <me-section class={`${COMPONENT_NAME} sn-call-to-action-type-2`} role="region" aria-label="Call to Action">
          <div class={`${COMPONENT_NAME}__content`}>
            <me-row yGap="3xl" yGapLg="none" xGap="none" alignLg="center">
              <me-column span="12" spanLg="6" class={`${COMPONENT_NAME}__intro`}>
                <div class={`${COMPONENT_NAME}__intro-inner`}>
                  <slot name="label"></slot>
                  <slot name="heading"></slot>
                  <me-row xGap="md" xGapLg="md" yGap="md" yGapLg="md" justify="center" justifyLg="start" class={`${COMPONENT_NAME}__actions`} onClick={this.handleActionClick}>
                    <me-column span="auto">
                      <slot name="action-primary"></slot>
                    </me-column>
                    <me-column span="auto">
                      <slot name="action-secondary"></slot>
                    </me-column>
                  </me-row>
                </div>
              </me-column>
              <me-column span="12" spanLg="6" class={`${COMPONENT_NAME}__media`}>
                <div class={`${COMPONENT_NAME}__media-inner`}>
                  <slot name="media"></slot>
                </div>
              </me-column>
            </me-row>
          </div>
        </me-section>
      </Host>
    );
  }
}
